import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, withModifiers as _withModifiers, withKeys as _withKeys, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "form-file-upload" }
const _hoisted_2 = ["aria-disabled", "onKeydown"]
const _hoisted_3 = { class: "form-file-upload__dropzone-text" }
const _hoisted_4 = {
  key: 0,
  class: "form-file-upload__list"
}
const _hoisted_5 = { class: "form-file-upload__item-icon" }
const _hoisted_6 = ["src"]
const _hoisted_7 = { class: "form-file-upload__item-body" }
const _hoisted_8 = { class: "form-file-upload__item-name" }
const _hoisted_9 = { class: "form-file-upload__item-meta" }
const _hoisted_10 = {
  key: 0,
  class: "form-file-upload__progress",
  "aria-hidden": "true"
}
const _hoisted_11 = {
  key: 1,
  class: "form-file-upload__item-error"
}
const _hoisted_12 = ["disabled", "onClick"]
const _hoisted_13 = ["src"]

import { message } from 'ant-design-vue';
import { computed, ref } from 'vue';
import {
  formatFileSize,
  getDeleteFileIconPath,
  getDocFormatIconPath,
  getFileExtension,
  getFileFormatLabel,
  isAllowedUploadExtension,
  FILE_UPLOAD_MAX_SIZE_BYTES,
} from '@/js/utils/file-upload-utils';

export type FileUploadItemStatus = 'uploading' | 'ready' | 'error';

export type FileUploadItem = {
  id: string;
  file: File;
  status: FileUploadItemStatus;
  progress: number;
  errorMessage?: string;
};

const acceptAttribute =
  '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation';
const MAX_UPLOAD_FILES = 5;

export default /*@__PURE__*/_defineComponent({
  __name: 'form-file-upload',
  props: {
    modelValue: { default: () => [] }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const deleteFileIconPath = getDeleteFileIconPath();
const maxFilesErrorMessage = `Можно загрузить не более ${MAX_UPLOAD_FILES} файлов`;

const fileInputRef = ref<HTMLInputElement | null>(null);
const isDragOver = ref(false);
const uploadTimers = new Map<string, number>();

const items = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});
const isLimitReached = computed(() => items.value.length >= MAX_UPLOAD_FILES);

function createItemId(): string {
  return `file-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function validateFile(file: File): { valid: boolean; errorMessage?: string } {
  const extension = getFileExtension(file.name);

  if (!isAllowedUploadExtension(extension)) {
    return { valid: false };
  }

  if (file.size > FILE_UPLOAD_MAX_SIZE_BYTES) {
    return {
      valid: false,
      errorMessage: 'Размер файла не должен превышать 100 Мб',
    };
  }

  return { valid: true };
}

function clearUploadTimer(id: string): void {
  const timerId = uploadTimers.get(id);
  if (timerId !== undefined) {
    window.clearInterval(timerId);
    uploadTimers.delete(id);
  }
}

function simulateUpload(item: FileUploadItem): void {
  const durationMs = 600;
  const startedAt = Date.now();

  const timerId = window.setInterval(() => {
    const elapsed = Date.now() - startedAt;
    const progress = Math.min(100, Math.round((elapsed / durationMs) * 100));
    const nextItems = items.value.map((entry) =>
      entry.id === item.id ? { ...entry, progress } : entry,
    );
    items.value = nextItems;

    if (progress >= 100) {
      clearUploadTimer(item.id);
      items.value = items.value.map((entry) =>
        entry.id === item.id ? { ...entry, status: 'ready' as const, progress: 100 } : entry,
      );
    }
  }, 50);

  uploadTimers.set(item.id, timerId);
}

function addFiles(fileList: FileList | File[]): void {
  const incoming = Array.from(fileList);
  if (!incoming.length) return;

  const availableSlots = MAX_UPLOAD_FILES - items.value.length;
  if (availableSlots <= 0) {
    message.warning(maxFilesErrorMessage);
    return;
  }

  if (incoming.length > availableSlots) {
    message.warning(maxFilesErrorMessage);
  }

  const filesToAdd = incoming.slice(0, availableSlots);
  const nextItems = [...items.value];
  const uploadsToStart: FileUploadItem[] = [];

  filesToAdd.forEach((file) => {
    const validation = validateFile(file);
    const id = createItemId();

    if (!validation.valid) {
      nextItems.push({
        id,
        file,
        status: 'error',
        progress: 0,
        errorMessage: validation.errorMessage,
      });
      return;
    }

    const item: FileUploadItem = {
      id,
      file,
      status: 'uploading',
      progress: 0,
    };
    nextItems.push(item);
    uploadsToStart.push(item);
  });

  items.value = nextItems;
  uploadsToStart.forEach((item) => simulateUpload(item));
}

function openFileDialog(): void {
  if (isLimitReached.value) {
    message.warning(maxFilesErrorMessage);
    return;
  }

  fileInputRef.value?.click();
}

function onInputChange(event: Event): void {
  const input = event.target as HTMLInputElement;
  if (input.files?.length) {
    addFiles(input.files);
  }
  input.value = '';
}

function onDrop(event: DragEvent): void {
  isDragOver.value = false;
  if (event.dataTransfer?.files?.length) {
    addFiles(event.dataTransfer.files);
  }
}

function onDragEnter(): void {
  if (isLimitReached.value) return;
  isDragOver.value = true;
}

function onDragLeave(event: DragEvent): void {
  const related = event.relatedTarget as Node | null;
  const current = event.currentTarget as HTMLElement;
  if (related && current.contains(related)) return;
  isDragOver.value = false;
}

function removeItem(id: string): void {
  clearUploadTimer(id);
  items.value = items.value.filter((item) => item.id !== id);
}

function reset(): void {
  items.value.forEach((item) => clearUploadTimer(item.id));
  items.value = [];
}

function getReadyFiles(): File[] {
  return items.value.filter((item) => item.status === 'ready').map((item) => item.file);
}

function setSubmittingState(isSubmitting: boolean): void {
  if (!isSubmitting) return;

  items.value = items.value.map((item) =>
    item.status === 'ready' ? { ...item, status: 'uploading' as const, progress: 0 } : item,
  );

  items.value
    .filter((item) => item.status === 'uploading' && item.progress === 0)
    .forEach((item) => simulateUpload(item));
}

function resetSubmittingState(): void {
  items.value.forEach((item) => clearUploadTimer(item.id));
  items.value = items.value.map((item) =>
    item.status === 'uploading' ? { ...item, status: 'ready' as const, progress: 100 } : item,
  );
}

__expose({
  reset,
  getReadyFiles,
  setSubmittingState,
  resetSubmittingState,
});

return (_ctx: any,_cache: any) => {
  const _component_a_spin = _resolveComponent("a-spin")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: _normalizeClass(["form-file-upload__dropzone", {
        'form-file-upload__dropzone--active': isDragOver.value,
        'form-file-upload__dropzone--disabled': isLimitReached.value,
      }]),
      role: "button",
      tabindex: "0",
      "aria-disabled": isLimitReached.value,
      onClick: openFileDialog,
      onKeydown: [
        _withKeys(_withModifiers(openFileDialog, ["prevent"]), ["enter"]),
        _withKeys(_withModifiers(openFileDialog, ["prevent"]), ["space"])
      ],
      onDragenter: _withModifiers(onDragEnter, ["prevent"]),
      onDragover: _withModifiers(onDragEnter, ["prevent"]),
      onDragleave: _withModifiers(onDragLeave, ["prevent"]),
      onDrop: _withModifiers(onDrop, ["prevent"])
    }, [
      _createElementVNode("p", _hoisted_3, [
        _createElementVNode("button", {
          type: "button",
          class: "form-file-upload__dropzone-link",
          onClick: _withModifiers(openFileDialog, ["stop"])
        }, [...(_cache[0] || (_cache[0] = [
          _createElementVNode("span", { class: "form-file-upload__dropzone-desktop-text" }, "Выберите", -1),
          _createElementVNode("span", { class: "form-file-upload__dropzone-mobile-text" }, "Выбрать файлы", -1)
        ]))]),
        _cache[1] || (_cache[1] = _createElementVNode("span", { class: "form-file-upload__dropzone-desktop-text" }, " или перетащите файлы", -1))
      ]),
      _cache[2] || (_cache[2] = _createElementVNode("p", { class: "form-file-upload__dropzone-hint form-file-upload__dropzone-hint--desktop" }, " Допустимы только PDF, DOC, XLS, PPT до 100 Мб, не более 5 файлов ", -1)),
      _cache[3] || (_cache[3] = _createElementVNode("p", { class: "form-file-upload__dropzone-hint form-file-upload__dropzone-hint--mobile" }, " PDF, DOC, XLS до 100 Мб ", -1)),
      _createElementVNode("input", {
        ref_key: "fileInputRef",
        ref: fileInputRef,
        type: "file",
        class: "form-file-upload__input",
        multiple: "",
        accept: acceptAttribute,
        onChange: onInputChange
      }, null, 544)
    ], 42, _hoisted_2),
    (items.value.length)
      ? (_openBlock(), _createElementBlock("ul", _hoisted_4, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(items.value, (item) => {
            return (_openBlock(), _createElementBlock("li", {
              key: item.id,
              class: _normalizeClass(["form-file-upload__item", {
          'form-file-upload__item--error': item.status === 'error',
          'form-file-upload__item--uploading': item.status === 'uploading',
        }])
            }, [
              _createElementVNode("div", _hoisted_5, [
                (item.status === 'uploading')
                  ? (_openBlock(), _createBlock(_component_a_spin, {
                      key: 0,
                      size: "small"
                    }))
                  : (_openBlock(), _createElementBlock("img", {
                      key: 1,
                      src: _unref(getDocFormatIconPath)(item.file.name)
                    }, null, 8, _hoisted_6))
              ]),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("p", _hoisted_8, "Файл. " + _toDisplayString(item.file.name), 1),
                _createElementVNode("p", _hoisted_9, _toDisplayString(_unref(getFileFormatLabel)(item.file.name)) + " / " + _toDisplayString(_unref(formatFileSize)(item.file.size)), 1),
                (item.status === 'uploading')
                  ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                      _createElementVNode("span", {
                        class: "form-file-upload__progress-bar",
                        style: _normalizeStyle({ width: `${item.progress}%` })
                      }, null, 4)
                    ]))
                  : (item.status === 'error' && item.errorMessage)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_11, _toDisplayString(item.errorMessage), 1))
                    : _createCommentVNode("", true)
              ]),
              _createElementVNode("button", {
                type: "button",
                class: "form-file-upload__remove",
                "aria-label": "Удалить файл",
                disabled: item.status === 'uploading',
                onClick: ($event: any) => (removeItem(item.id))
              }, [
                _createElementVNode("img", {
                  src: _unref(deleteFileIconPath),
                  class: "form-file-upload__remove-icon",
                  width: "24",
                  height: "24"
                }, null, 8, _hoisted_13)
              ], 8, _hoisted_12)
            ], 2))
          }), 128))
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})